/**
 * useDealExtract - background AI extraction hook for the LiveMeetingPage right rail.
 *
 * Loads all questionnaire sections + fields for the given deal, then runs
 * a debounced extraction API call whenever new finalized transcript chunks arrive.
 * State updates are isolated to this hook and never block the transcript rendering
 * path - the hook only touches its own state, which is separate from `useLiveMeetingSession`.
 *
 * Gate fields (aiEnabled = false) are loaded for visibility evaluation and rendered
 * as context labels in the panel; they do not count toward totalCount/answeredCount.
 * DB-answered fields are preserved across session resets (only live-captured answers
 * are cleared when a new session starts).
 *
 * Log tag: [LiveMeetingExtract]
 */

import * as React from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import type { ConditionalRule } from '../../sales/services/salesApi';
import { salesApi } from '../../sales/services/salesApi';
import {
  getFieldNestingLevelByRules,
  isFieldEnabledByRules,
  isFieldVisibleByRules,
} from '../../sales/utils/questionnaire-field-visibility';
import type { FieldCoachingState, LiveMeetingStatus, TranscriptChunk } from '../types/live-meeting';

// ── Public types ───────────────────────────────────────────────────────────

/**
 * A dependent field whose answer is shown inline under its parent field
 * rather than as a separate tracked item.
 */
export interface ExtractChildField {
  fieldKey: string;
  label: string;
  uiControl: string;
  options: Array<{
    label: string;
    value: string;
    optionType?: string | null;
    description?: string | null;
  }>;
  state: FieldCoachingState;
  /** True when this child's controlling conditions are currently satisfied. */
  conditionMet: boolean;
}

export interface ExtractSectionField {
  fieldKey: string;
  label: string;
  description: string | null;
  uiControl: string;
  options: Array<{
    label: string;
    value: string;
    optionType?: string | null;
    description?: string | null;
  }>;
  state: FieldCoachingState;
  /** True for gate (non-aiEnabled) fields - rendered as context labels, not tracked items. */
  isGate: boolean;
  nestingLevel: number;
  /**
   * Dependent child fields (controlled by show/enable rules targeting them from this field).
   * Their answers are rendered inline under this field when conditions are met, not as
   * separate tracked items in the panel.
   */
  children?: ExtractChildField[];
}

export interface ExtractQuestion {
  groupKey: string;
  label: string;
  fields: ExtractSectionField[];
}

export interface ExtractSection {
  sectionKey: string;
  label: string;
  displayOrder: number;
  groups: ExtractQuestion[];
}

export interface UseDealExtractReturn {
  sections: ExtractSection[];
  totalCount: number;
  answeredCount: number;
  /** Answers captured this session by AI (source = 'live'). Used for auto-save on end. */
  answeredValues: Record<string, string>;
  isLoading: boolean;
  /** True while an AI extraction call is in-flight. */
  isInferring: boolean;
  /** Dev-only: clears all live-captured answers so all fields return to pending. */
  reset: () => void;
}

// ── Internal types ─────────────────────────────────────────────────────────

interface FieldMeta {
  fieldKey: string;
  label: string;
  description: string | null;
  uiControl: string;
  options: Array<{
    label: string;
    value: string;
    optionType?: string | null;
    description?: string | null;
  }>;
  isGate: boolean;
  /** True when the field has a canonical integration mapping - inferred from external data, not asked in the meeting. */
  hasCanonical: boolean;
  nestingLevel: number;
}

interface QuestionMeta {
  groupKey: string;
  label: string;
  fieldKeys: string[];
}

interface SectionMeta {
  sectionKey: string;
  label: string;
  displayOrder: number;
  groups: QuestionMeta[];
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useDealExtract({
  transcriptChunks,
  sessionStatus,
  dealUuid,
}: {
  transcriptChunks: TranscriptChunk[];
  sessionStatus: LiveMeetingStatus;
  dealUuid?: string | null;
}): UseDealExtractReturn {
  const { user } = useAuth();

  /** All fields (gate + aiEnabled) - used for visibility filtering and panel rendering. */
  const [allFields, setAllFields] = React.useState<FieldMeta[]>([]);

  /** Section structure: preserves questionnaire grouping for the UI. */
  const [sectionMeta, setSectionMeta] = React.useState<SectionMeta[]>([]);

  /** Conditional rules from the questionnaire, used for visibility and nesting evaluation. */
  const [conditionalRules, setConditionalRules] = React.useState<ConditionalRule[]>([]);

  /** Per-field coaching state. */
  const [fieldStates, setFieldStates] = React.useState<Record<string, FieldCoachingState>>({});

  const [isLoading, setIsLoading] = React.useState(false);
  const [isInferring, setIsInferring] = React.useState(false);

  // Guards that prevent duplicate or overlapping extraction calls.
  const extractTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastChunkCountRef = React.useRef(0);
  const isExtractingRef = React.useRef(false);

  // Always holds the latest transcript - lets the finally-block retry avoid
  // stale closures when a new chunk arrives during an in-flight API call.
  const transcriptChunksRef = React.useRef(transcriptChunks);
  transcriptChunksRef.current = transcriptChunks;
  const userDisplayNameRef = React.useRef(user?.displayName ?? null);
  userDisplayNameRef.current = user?.displayName ?? null;

  // ── Load questionnaire from workspace default template or linked deal ─────

  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    // Reset extraction state when questionnaire source changes
    lastChunkCountRef.current = 0;

    const load = dealUuid
      ? salesApi.getDealQuestionnaire(dealUuid)
      : salesApi.getLiveMeetingQuestionnaire();

    load
      .then(({ questionnaire }) => {
        if (cancelled) return;

        const fields: FieldMeta[] = [];
        const sections: SectionMeta[] = [];
        const initial: Record<string, FieldCoachingState> = {};

        for (const section of questionnaire.sections) {
          const questionMeta: QuestionMeta[] = [];

          for (const question of section.groups) {
            const fieldKeys: string[] = [];

            for (const field of question.fields) {
              const isGate = !field.aiEnabled;
              const nestingLevel = getFieldNestingLevelByRules(
                field.fieldKey,
                questionnaire.conditionalRules
              );

              fields.push({
                fieldKey: field.fieldKey,
                label: field.label,
                description: field.description,
                uiControl: field.uiControl,
                options: field.options.map((o) => ({
                  label: o.label,
                  value: o.value,
                  optionType: o.optionType ?? null,
                  description: o.description ?? null,
                })),
                isGate,
                hasCanonical: field.canonical != null,
                nestingLevel,
              });
              fieldKeys.push(field.fieldKey);

              if (field.value != null) {
                // Pre-populate from DB value (works for gate and non-gate fields alike).
                // Do NOT mark as asked - the AI should still try to capture these live;
                // only live-captured answers are tracked.
                // Use JSON.stringify for object values so complex fields (checklist,
                // select_matrix, radio_text_list) round-trip correctly through formatAnswer.
                const answerStr =
                  typeof field.value === 'object'
                    ? JSON.stringify(field.value)
                    : String(field.value);
                initial[field.fieldKey] = {
                  status: 'answered',
                  answer: answerStr,
                  source: 'db',
                };
              } else if (isGate) {
                initial[field.fieldKey] = { status: 'gate' };
              } else {
                initial[field.fieldKey] = { status: 'pending' };
              }
            }

            questionMeta.push({
              groupKey: question.groupKey,
              label: question.label,
              fieldKeys,
            });
          }

          sections.push({
            sectionKey: section.sectionKey,
            label: section.label,
            displayOrder: section.displayOrder,
            groups: questionMeta,
          });
        }

        setAllFields(fields);
        setSectionMeta(sections);
        setConditionalRules(questionnaire.conditionalRules);
        setFieldStates(initial);
      })
      .catch((err: unknown) => {
        console.error('[LiveMeetingExtract] failed to load questionnaire', err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Re-run when the linked deal changes so the questionnaire reloads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealUuid]);

  // ── Reset on new session - preserve DB answers ────────────────────────────

  React.useEffect(() => {
    if (sessionStatus !== 'active') return;
    lastChunkCountRef.current = 0;
    setFieldStates((prev) => {
      const reset: Record<string, FieldCoachingState> = {};
      for (const key of Object.keys(prev)) {
        const prevState = prev[key];
        // Preserve DB-sourced answers and gate states - only live captures are cleared.
        if (prevState?.status === 'answered' && prevState.source === 'db') {
          reset[key] = prevState;
        } else if (prevState?.status === 'gate') {
          reset[key] = prevState;
        } else {
          reset[key] = { status: 'pending' };
        }
      }
      return reset;
    });
  }, [sessionStatus]);

  // ── Debounced coaching trigger ────────────────────────────────────────────

  /**
   * Runs one extraction API call against `chunks`. If a call is already in-flight,
   * it skips - but the `finally` block will re-invoke once the current call lands
   * if more chunks accumulated in the meantime, so no answer is silently dropped.
   */
  const runExtractCall = React.useCallback(
    (chunks: TranscriptChunk[]) => {
      if (isExtractingRef.current) return;
      isExtractingRef.current = true;
      setIsInferring(true);
      lastChunkCountRef.current = chunks.length;

      const recentChunks = chunks
        .slice(-10)
        .map((c) => ({ userName: c.userName, text: c.text, role: c.role }));

      salesApi
        .extractLiveMeetingDefault({
          recentChunks,
          hostName: userDisplayNameRef.current,
        })
        .then(({ results }) => {
          setFieldStates((prev) => {
            const next = { ...prev };
            for (const item of results) {
              if (item.type === 'answer_extracted') {
                const prevState = prev[item.fieldKey];
                const previousDbAnswer =
                  prevState?.status === 'answered' && prevState.source === 'db'
                    ? prevState.answer
                    : undefined;
                next[item.fieldKey] = {
                  status: 'answered',
                  answer: item.answer,
                  source: 'live',
                  ...(previousDbAnswer !== undefined ? { previousDbAnswer } : {}),
                };
              }
              // question_suggestion is intentionally ignored - fields stay pending so
              // the AI continues to re-evaluate them on future transcript chunks.
            }
            return next;
          });
        })
        .catch((err: unknown) => {
          console.error('[LiveMeetingExtract] API error', err);
        })
        .finally(() => {
          isExtractingRef.current = false;
          setIsInferring(false);
          // If new chunks arrived while this call was in-flight, schedule a follow-up
          // so the answer is never silently dropped due to the guard above.
          const latest = transcriptChunksRef.current;
          if (latest.length > lastChunkCountRef.current) {
            if (extractTimerRef.current) clearTimeout(extractTimerRef.current);
            extractTimerRef.current = setTimeout(() => {
              runExtractCall(transcriptChunksRef.current);
            }, 300);
          }
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  React.useEffect(() => {
    // Only fire when there are aiEnabled fields to extract against.
    const aiFieldCount = allFields.filter((f) => !f.isGate).length;
    if (aiFieldCount === 0) return;
    if (sessionStatus !== 'active') return;

    const newCount = transcriptChunks.length;
    if (newCount <= lastChunkCountRef.current) return;

    if (extractTimerRef.current) clearTimeout(extractTimerRef.current);

    extractTimerRef.current = setTimeout(() => {
      runExtractCall(transcriptChunksRef.current);
    }, 900);

    return () => {
      if (extractTimerRef.current) clearTimeout(extractTimerRef.current);
    };
    // transcriptChunks.length drives the debounce; allFields.length gates it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcriptChunks.length, sessionStatus, allFields.length, runExtractCall]);

  // ── Derive sections for rendering ─────────────────────────────────────────

  const sections = React.useMemo((): ExtractSection[] => {
    const fieldMap = new Map(allFields.map((f) => [f.fieldKey, f]));

    // Build answer map for visibility evaluation (keyed by fieldKey)
    const answerMap: Record<string, unknown> = {};
    for (const [key, state] of Object.entries(fieldStates)) {
      if (state.status === 'answered') {
        answerMap[key] = state.answer;
      }
    }

    // ── Build dependency maps from show/enable rules ──────────────────────
    // Fields that are targeted by any show/enable rule are "dependent" - they
    // will be hidden from the top-level tracked list and instead rendered
    // inline under their controlling parent field.
    const dependentFieldKeys = new Set<string>();
    const parentToDirectChildren = new Map<string, string[]>();
    for (const rule of conditionalRules) {
      if (rule.effect !== 'show' && rule.effect !== 'enable') continue;
      dependentFieldKeys.add(rule.targetFieldKey);
      const list = parentToDirectChildren.get(rule.sourceFieldKey) ?? [];
      if (!list.includes(rule.targetFieldKey)) list.push(rule.targetFieldKey);
      parentToDirectChildren.set(rule.sourceFieldKey, list);
    }

    // BFS: collect all descendants of a field within an allowed key set
    function getDescendants(fieldKey: string, allowedKeys: Set<string>): string[] {
      const result: string[] = [];
      const queue = [...(parentToDirectChildren.get(fieldKey) ?? [])];
      const visited = new Set<string>([fieldKey]);
      while (queue.length > 0) {
        const next = queue.shift()!;
        if (visited.has(next)) continue;
        visited.add(next);
        if (allowedKeys.has(next)) {
          result.push(next);
          const children = parentToDirectChildren.get(next) ?? [];
          queue.push(...children);
        }
      }
      return result;
    }

    return (
      sectionMeta
        .map((s) => ({
          sectionKey: s.sectionKey,
          label: s.label,
          displayOrder: s.displayOrder,
          groups: s.groups
            .map((q) => {
              const groupFieldKeySet = new Set(q.fieldKeys);

              // Only include non-dependent fields as top-level tracked items.
              // Dependent fields (controlled by show/enable rules) are attached
              // as inline children on their parent field instead.
              const visibleFields = q.fieldKeys
                .filter((k) => {
                  if (dependentFieldKeys.has(k)) return false;
                  return isFieldVisibleByRules(k, conditionalRules, answerMap);
                })
                .map((k): ExtractSectionField | null => {
                  const fm = fieldMap.get(k);
                  if (!fm) return null;
                  // Canonical fields are sourced from integrations - never tracked in a meeting.
                  if (fm.hasCanonical) return null;

                  // Build inline children from all descendants in this question group
                  const descendants = getDescendants(k, groupFieldKeySet);
                  const children: ExtractChildField[] = descendants
                    .map((ck): ExtractChildField | null => {
                      const cfm = fieldMap.get(ck);
                      if (!cfm || cfm.isGate || cfm.hasCanonical) return null;
                      const conditionMet =
                        isFieldVisibleByRules(ck, conditionalRules, answerMap) &&
                        isFieldEnabledByRules(ck, conditionalRules, answerMap);
                      return {
                        fieldKey: cfm.fieldKey,
                        label: cfm.label,
                        uiControl: cfm.uiControl,
                        options: cfm.options,
                        state:
                          fieldStates[ck] ?? ({ status: 'pending' } satisfies FieldCoachingState),
                        conditionMet,
                      };
                    })
                    .filter((c): c is ExtractChildField => c !== null);

                  return {
                    fieldKey: fm.fieldKey,
                    label: fm.label,
                    description: fm.description,
                    uiControl: fm.uiControl,
                    options: fm.options,
                    state: fieldStates[k] ?? ({ status: 'pending' } satisfies FieldCoachingState),
                    isGate: fm.isGate,
                    nestingLevel: fm.nestingLevel,
                    children: children.length > 0 ? children : undefined,
                  };
                })
                .filter((f): f is ExtractSectionField => f !== null);

              // Only keep AI-enabled (non-gate) fields - gate fields are not tracked in the panel.
              const fields = visibleFields.filter((f) => !f.isGate);

              return { groupKey: q.groupKey, label: q.label, fields };
            })
            // Drop groups that only have gate fields or are entirely hidden
            .filter((q) => q.fields.length > 0),
        }))
        // Drop sections with no visible ai-tracked groups
        .filter((s) => s.groups.length > 0)
    );
  }, [sectionMeta, allFields, fieldStates, conditionalRules]);

  const totalCount = allFields.filter((f) => !f.isGate && !f.hasCanonical).length;
  const answeredCount = allFields.filter(
    (f) => !f.isGate && !f.hasCanonical && fieldStates[f.fieldKey]?.status === 'answered'
  ).length;

  const answeredValues = React.useMemo(() => {
    const result: Record<string, string> = {};
    for (const [key, state] of Object.entries(fieldStates)) {
      if (state.status === 'answered' && state.source === 'live') {
        result[key] = state.answer;
      }
    }
    return result;
  }, [fieldStates]);

  const reset = React.useCallback(() => {
    lastChunkCountRef.current = 0;
    isExtractingRef.current = false;
    if (extractTimerRef.current) {
      clearTimeout(extractTimerRef.current);
      extractTimerRef.current = null;
    }
    setFieldStates((prev) => {
      const next: Record<string, FieldCoachingState> = {};
      for (const [key, state] of Object.entries(prev)) {
        // Preserve DB-sourced answers and gate states; clear live-captured answers.
        if (state.status === 'answered' && state.source === 'db') {
          next[key] = state;
        } else if (state.status === 'gate') {
          next[key] = state;
        } else {
          next[key] = { status: 'pending' };
        }
      }
      return next;
    });
    // Schedule extraction immediately so the panel refills from the current transcript.
    extractTimerRef.current = setTimeout(() => {
      runExtractCall(transcriptChunksRef.current);
    }, 100);
  }, [runExtractCall]);

  return { sections, totalCount, answeredCount, answeredValues, isLoading, isInferring, reset };
}
